import css, { get } from '@styled-system/css'

const derivePath = (args, props) => {
  if (!Array.isArray(args)) {
    args = [args]
  }
  return args.map(arg => (
    typeof arg === 'function'
      ? arg(props)
      : arg
  )).join('.')
}

/**
 * Creates a function for styled-components/macro rules to look up values from
 * the theme.
 *
 * Accepts two args, the desired theme key and an optional fallback.  Both
 * can be derived from functions, or arrays containing functions, that
 * accept the props object.
 *
 * For example, given a theme scale like this:
 *
 *     variants: {
 *        foo: 'cool',
 *        bar: 'rad',
 *     }
 *
 * ...and a component that accepted a prop `variant`. You could write a
 * styled-component style like:
 *
 *     color: ${themeGet(['variants', props => props.variant], 'blue')};
 *
 * ...as an array where 'variants' is static and the variant name is a
 * function which returns the variant prop.
 *
 * The same thing can be expressed like:
 *
 *     color: ${themeGet(props => `variants.${props.variant}`, 'blue')};
 *
 * ...where a single function is passed that derives the whole path.
 *
 * The fallback can also be a theme path, or if necessary for some reason,
 * an array or function the same as the desired key.
 *
 * **If** the resulting value from the theme is an object, it will be passed
 * through the `css` function so scales and other theme lookups will be
 * applied. For example given a theme:
 *
 *     space: [0, '4px', '8px'],
 *     styles: {
 *       h1: {
 *         mb: 2,
 *       }
 *     }
 *
 *  The call `{themeGet('styles.h1')}` will result in:
 *
 *     margin-botom: 8px;
 */
export const themeGet = (key, fallback) => props => {
  const theme = props.theme || props

  if (typeof key === 'object') {
    return css(key)(theme)
  }

  fallback = fallback ? derivePath(fallback, props) : undefined

  const value = get(
    theme,
    derivePath(key, props),
    fallback ? get(theme, fallback, fallback) : undefined
  )

  return typeof value === 'object' ? css(value)(theme) : value
}

/**
 * Creates a template literal tag for a given theme scale which in turn returns
 * a function expecting `theme` which it uses to look up scaled values.
 *
 * The intention of this function is to make it easier to write shorthand form
 * or complex styles styles that depend on theme when using the `css` prop,
 * which if given a function, will callback that function with the theme.
 *
 * Say you need to get at scale values for space to write a `calc` rule, which
 * is not unreasonable. Using `get` from styled-system css, you could write
 * this:
 *
 *     css({
 *       width: theme => `calc(100% - ${get(theme, 'space.2')})`
 *     })
 *
 * ...but it feels pretty bad.  Using this convenience function, it gets a
 * little cleaner:
 *
 *     css({
 *       width: createThemeTag('space')`calc(100% - ${2})`
 *     })
 *
 * But using a predefined tag, it gets lot cleaner:
 *
 *     const space = createThemeTag('space')
 *
 *     //...
 *
 *     css({
 *       width: space`calc(100% - ${2})`
 *     })
 */
export const createThemeTag = scale => (strings, ...vals) => props => {
  const theme = props.theme || props
  let i = -1
  const t = scale
    ? v => scale + '.' + v
    : v => v

  return strings.reduce((memo, str) => {
    i += 1
    const val = typeof vals[i] === 'function' ? vals[i](props) : vals[i]
    return val
      ? [...memo, str, get(theme, t(val), val)]
      : [...memo, str]
  }, []).join('')
}

export const tags = {
  /**
   * Scale-less theme tag which does not prefix the theme path and can be used
   * for any scale/object, or different scales at the same time.
   *
   * E.g.
   *
   *     border: tags.themed`${'borders.0'} solid ${'colors.hotpink'}`
   */
  themed: createThemeTag(),

  /**
   * Theme tag which transforms values from the `space` scale.
   *
   * E.g.
   *
   *     width: tags.space`calc(100% - ${2})`
   */
  space: createThemeTag('space'),
}
